import { attachUpload } from "@/api";
import { uploadFile } from "@/utils/uploadFile";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { UploadFileOptions } from "@/utils/uploadFile";
import type { FileAttachmentNodeAttrs } from "@corca/tiptap";

// Mirrors DirectUploadsController::BYTE_SIZE_LIMIT
export const FILE_ATTACHMENT_MAX_SIZE = 104_857_600;

/**
 * Uploads a non-image file via direct upload and attaches it to the scope
 * entity (document or chat).
 *
 * @param file - The file to upload
 * @param scope - The document or chat to attach the file to
 * @param options - Upload options, e.g. an `onProgress` callback
 * @returns File attachment node attrs or null if the file is empty, too large or the upload fails
 */
export async function uploadFileAttachment(
  file: File,
  scope: UploadScope,
  options: UploadFileOptions = {},
): Promise<FileAttachmentNodeAttrs | null> {
  if (file.size === 0 || file.size > FILE_ATTACHMENT_MAX_SIZE) {
    console.warn(
      `Cannot attach ${file.name}: file is empty or exceeds ${FILE_ATTACHMENT_MAX_SIZE} bytes.`,
    );
    return null;
  }

  try {
    const key = await uploadFile(file, options);
    await attachUpload(scope.type, scope.uid, key, undefined, { signal: options.signal });

    return {
      key,
      filename: file.name,
      contentType: file.type || "application/octet-stream",
      byteSize: file.size,
      sourceUid: null,
      sourceType: null,
    };
  } catch (error) {
    if (!options.signal?.aborted) {
      console.error("Failed to upload file attachment", error);
    }
    return null;
  }
}
